/**
 * Syntax-highlighted code block with copy button.
 * Uses react-shiki with dual themes for light/dark mode.
 *
 * Supports "code-group:" language prefix for tabbed multi-language blocks
 * (produced by groupCodeBlocks preprocessor).
 */

import { Check, Copy } from "lucide-react";
import { useTheme } from "next-themes";
import { useState } from "react";
import { ShikiHighlighter } from "react-shiki";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const LANG_LABELS: Record<string, string> = {
	python: "Python",
	bash: "cURL",
	javascript: "JavaScript",
	typescript: "TypeScript",
	json: "JSON",
};

interface CodeBlockProps {
	className?: string;
	children?: React.ReactNode;
}

export function CodeBlock({ className, children, ...props }: CodeBlockProps) {
	const [copied, setCopied] = useState(false);
	const { resolvedTheme } = useTheme();
	const shikiTheme = resolvedTheme === "dark" ? "github-dark" : "github-light";

	// Extract language from className (e.g., "language-typescript")
	const match = /language-([\w|:-]+)/.exec(className || "");
	const language = match?.[1] || "text";
	const code = String(children).replace(/\n$/, "");

	// Inline code (no language fence)
	if (!match) {
		return (
			<code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
				{children}
			</code>
		);
	}

	// Code group — tabbed multi-language block
	if (language.startsWith("code-group:")) {
		const langs = language.replace("code-group:", "").split("|");
		const codes = code.split("---CODESPLIT---\n");
		return <CodeGroupTabs langs={langs} codes={codes} shikiTheme={shikiTheme} />;
	}

	const handleCopy = async () => {
		await navigator.clipboard.writeText(code);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="relative group/code my-4 rounded-lg border border-border/50 overflow-hidden">
			{/* Header with language badge and copy button */}
			<div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-b border-border/30">
				<span className="text-xs font-mono text-muted-foreground">
					{LANG_LABELS[language] || language}
				</span>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6 opacity-0 group-hover/code:opacity-100 transition-opacity"
					onClick={handleCopy}
					aria-label="Copy code"
				>
					{copied ? (
						<Check className="h-3.5 w-3.5 text-green-500" />
					) : (
						<Copy className="h-3.5 w-3.5" />
					)}
				</Button>
			</div>

			{/* Code block with shiki highlighting */}
			<div
				className={cn(
					"overflow-x-auto text-sm bg-muted/20 dark:bg-card/30",
					"[&_pre]:!bg-transparent [&_pre]:p-4 [&_pre]:m-0"
				)}
			>
				<ShikiHighlighter
					language={language}
					theme={shikiTheme}
					delay={100}
					showLanguage={false}
					addDefaultStyles={false}
				>
					{code}
				</ShikiHighlighter>
			</div>
		</div>
	);
}

/** Tabbed code block for grouped languages. */
function CodeGroupTabs({
	langs,
	codes,
	shikiTheme,
}: {
	langs: string[];
	codes: string[];
	shikiTheme: string;
}) {
	const [activeTab, setActiveTab] = useState(langs[0]);
	const [copied, setCopied] = useState(false);

	const activeIndex = langs.indexOf(activeTab);
	const activeCode = (codes[activeIndex] || "").replace(/\n$/, "");

	const handleCopy = async () => {
		await navigator.clipboard.writeText(activeCode);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="relative group/code my-4 rounded-lg border border-border/50 overflow-hidden">
			<div className="flex items-center justify-between px-3 py-1 bg-muted/30 border-b border-border/30">
				<Tabs value={activeTab} onValueChange={setActiveTab} className="flex-col gap-0">
					<TabsList className="h-7 bg-transparent p-0">
						{langs.map((lang) => (
							<TabsTrigger key={lang} value={lang} className="h-6 px-2 text-xs font-mono">
								{LANG_LABELS[lang] || lang}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6 opacity-0 group-hover/code:opacity-100 transition-opacity"
					onClick={handleCopy}
					aria-label="Copy code"
				>
					{copied ? (
						<Check className="h-3.5 w-3.5 text-green-500" />
					) : (
						<Copy className="h-3.5 w-3.5" />
					)}
				</Button>
			</div>

			{langs.map((lang, idx) => (
				<div
					key={lang}
					className={cn(
						"overflow-x-auto text-sm bg-muted/20 dark:bg-card/30",
						"[&_pre]:!bg-transparent [&_pre]:p-4 [&_pre]:m-0",
						lang !== activeTab && "hidden"
					)}
				>
					<ShikiHighlighter
						language={lang}
						theme={shikiTheme}
						delay={100}
						showLanguage={false}
						addDefaultStyles={false}
					>
						{(codes[idx] || "").replace(/\n$/, "")}
					</ShikiHighlighter>
				</div>
			))}
		</div>
	);
}
